import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Plus, Search, Hammer, LayoutGrid, List as ListIcon, CalendarDays } from "lucide-react";
import JobsKanban from "../components/JobsKanban";
import JobsCalendar from "../components/JobsCalendar";
import { statusLabel, statusBadge, JOB_STATUSES } from "../lib/jobStatus";
import { useAuth } from "../context/AuthContext";
import { isTech } from "../lib/roles";
import { Fab } from "../components/Fab";
import { PayModeBadge } from "../lib/payMode";
import { fmtKc } from "../lib/format";

const fmt = fmtKc;

export default function Jobs() {
    const { user } = useAuth();
    const tech = isTech(user);
    const [jobs, setJobs] = useState([]);
    const [tags, setTags] = useState([]);
    const [activeTag, setActiveTag] = useState(null);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState(() => localStorage.getItem("jobs_view") || "list");
    const [statusFilter, setStatusFilter] = useState(null);
    const [showArchive, setShowArchive] = useState(false);

    const load = () => api.get("/jobs").then((r) => { setJobs(r.data); setLoading(false); });
    useEffect(() => {
        load();
        api.get("/jobs/tags").then((r) => setTags(r.data)).catch(() => {});
    }, []);

    const setViewPersist = (v) => { setView(v); localStorage.setItem("jobs_view", v); };

    const filtered = jobs.filter((j) => {
        if (activeTag && !(j.tags || []).includes(activeTag)) return false;
        if (statusFilter && j.status !== statusFilter) return false;
        if (!statusFilter && !showArchive && j.status === "archiv" && view !== "kanban") return false;
        return !q || j.title?.toLowerCase().includes(q.toLowerCase()) || j.client_name?.toLowerCase().includes(q.toLowerCase());
    });
    const countByStatus = jobs.reduce((acc, j) => { acc[j.status] = (acc[j.status] || 0) + 1; return acc; }, {});

    const moveStatus = async (jobId, newStatus) => {
        const j = jobs.find((x) => x.id === jobId);
        if (!j || j.status === newStatus) return;
        // optimistic
        setJobs(jobs.map((x) => x.id === jobId ? { ...x, status: newStatus } : x));
        try {
            const payload = {
                title: j.title, client_id: j.client_id || null, client_name: j.client_name || "",
                client_address: j.client_address || "", client_phone: j.client_phone || "", client_email: j.client_email || "",
                site_address: j.site_address || "", description: j.description || "", status: newStatus,
                start_date: j.start_date || null, end_date: j.end_date || null, notes: j.notes || "",
                planned_revenue: j.planned_revenue || 0,
                tags: j.tags || [], warranty_months: j.warranty_months || 0, warranty_start_date: j.warranty_start_date || null,
                pay_mode: j.pay_mode || "hodinova", labor_price_base: j.labor_price_base || 0, default_vat_mode: j.default_vat_mode || "",
            };
            await api.put(`/jobs/${jobId}`, payload);
        } catch {
            load();
        }
    };

    return (
        <div className="px-10 py-10 max-w-7xl mx-auto animate-fade-in" data-testid="jobs-page">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <div className="label-eyebrow mb-2">Sekce</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900">Zakázky</h1>
                    <p className="text-sm text-slate-600 mt-2">List, kanban tabule i kalendář — tři pohledy na stejná data.</p>
                </div>
                {!tech && <Link to="/zakazky/nova" data-testid="new-job-button"
                      className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">
                    <Plus className="w-4 h-4" /> Nová zakázka
                </Link>}
            </div>

            {/* View toggle */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
                <div className="inline-flex border border-slate-300 bg-white">
                    <ViewBtn active={view === "list"} onClick={() => setViewPersist("list")} icon={ListIcon} testid="view-list">Seznam</ViewBtn>
                    <ViewBtn active={view === "kanban"} onClick={() => setViewPersist("kanban")} icon={LayoutGrid} testid="view-kanban">Kanban</ViewBtn>
                    <ViewBtn active={view === "calendar"} onClick={() => setViewPersist("calendar")} icon={CalendarDays} testid="view-calendar">Kalendář</ViewBtn>
                </div>
                <div className="flex-1"></div>
                <div className="flex items-center bg-white border border-slate-300 px-3 py-2 min-w-[280px]">
                    <Search className="w-4 h-4 text-slate-400 mr-2" />
                    <input placeholder="Hledat…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="jobs-search"
                           className="flex-1 text-sm focus:outline-none bg-transparent" />
                    <span className="text-xs text-slate-400">{filtered.length}/{jobs.length}</span>
                </div>
            </div>

            <div className="flex items-center gap-1.5 mb-4 flex-wrap" data-testid="status-filters">
                <button onClick={() => setStatusFilter(null)} data-testid="status-filter-all"
                        className={`text-xs px-2.5 py-1 border ${!statusFilter ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600 hover:bg-slate-100"}`}>
                    Vše
                </button>
                {JOB_STATUSES.map((s, i) => (
                    <div key={s.key} className="flex items-center gap-1.5">
                        {i > 0 && <span className="text-slate-300 text-xs">›</span>}
                        <button onClick={() => setStatusFilter(statusFilter === s.key ? null : s.key)} data-testid={`status-filter-${s.key}`}
                                className={`text-xs px-2.5 py-1 border inline-flex items-center gap-1.5 ${statusFilter === s.key ? "bg-slate-900 text-white border-slate-900" : `${s.badge} hover:opacity-80`}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${statusFilter === s.key ? "bg-white" : s.dot}`}></span>
                            {s.label} <span className="opacity-70">({countByStatus[s.key] || 0})</span>
                        </button>
                    </div>
                ))}
                {!statusFilter && view !== "kanban" && (countByStatus.archiv || 0) > 0 && (
                    <label className="ml-2 text-xs text-slate-500 inline-flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" checked={showArchive} onChange={(e) => setShowArchive(e.target.checked)} data-testid="show-archive" className="accent-orange-600" />
                        zobrazit archiv
                    </label>
                )}
            </div>

            {tags.length > 0 && (
                <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="tag-filters">
                    <span className="label-eyebrow">Filtrovat tag:</span>
                    <button onClick={() => setActiveTag(null)}
                            className={`text-xs px-2.5 py-1 border ${!activeTag ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600 hover:bg-slate-100"}`}>
                        Vše
                    </button>
                    {tags.map((t) => (
                        <button key={t.tag} onClick={() => setActiveTag(activeTag === t.tag ? null : t.tag)} data-testid={`tag-filter-${t.tag}`}
                                className={`text-xs px-2.5 py-1 border ${activeTag === t.tag ? "bg-orange-600 text-white border-orange-600" : "border-orange-300 text-orange-700 bg-orange-50 hover:bg-orange-100"}`}>
                            {t.tag} <span className="opacity-70">({t.count})</span>
                        </button>
                    ))}
                </div>
            )}

            {view === "list" && (
                <div className="bg-white border border-slate-200">
                    {loading ? (
                        <div className="p-12 text-center text-slate-400 text-xs uppercase tracking-widest">Načítání…</div>
                    ) : filtered.length === 0 ? (
                        <div className="p-16 text-center">
                            <Hammer className="w-12 h-12 text-slate-300 mx-auto mb-4" strokeWidth={1.5} />
                            <div className="font-display text-lg font-bold text-slate-700">Žádné zakázky</div>
                            <p className="text-sm text-slate-500 mt-1 mb-6">{tech ? "Zatím nejsou žádné zakázky." : "Začněte vytvořením první zakázky."}</p>
                            {!tech && <Link to="/zakazky/nova" className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">
                                <Plus className="w-4 h-4" /> Nová zakázka
                            </Link>}
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b-2 border-slate-900">
                                    <th className="px-6 py-3 font-semibold">Název</th>
                                    <th className="px-6 py-3 font-semibold">Klient</th>
                                    <th className="px-6 py-3 font-semibold">Místo</th>
                                    <th className="px-6 py-3 font-semibold">Stav</th>
                                    <th className="px-6 py-3 font-semibold">Odměna</th>
                                    {!tech && <th className="px-6 py-3 font-semibold text-right">Plán vs. realita</th>}
                                    {!tech && <th className="px-6 py-3 font-semibold text-right">Náklady</th>}
                                </tr>
                            </thead>
                            <tbody className="tabular-data">
                                {filtered.map((j, idx) => {
                                    const total = j.total_costs != null ? j.total_costs : (j.costs || []).reduce((s, c) => s + (c.total || c.quantity * c.unit_price || 0), 0) + (j.craftsmen_costs || 0);
                                    const planned = Number(j.plan_revenue != null ? j.plan_revenue : j.planned_revenue) || 0;
                                    const pct = planned > 0 ? Math.round((total / planned) * 100) : null;
                                    return (
                                        <tr key={j.id} data-testid={`job-row-${j.id}`} className={`border-b border-slate-100 hover:bg-orange-50/40 cursor-pointer ${idx % 2 ? "bg-slate-50/40" : ""}`}>
                                            <td className="px-6 py-3"><Link to={`/zakazky/${j.id}`} className="font-medium text-slate-900 hover:text-orange-600">{j.title}</Link></td>
                                            <td className="px-6 py-3 text-slate-600">{j.client_name || "—"}</td>
                                            <td className="px-6 py-3 text-slate-600">{j.site_address || "—"}</td>
                                            <td className="px-6 py-3">
                                                <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${statusBadge(j.status)}`}>
                                                    {statusLabel(j.status)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-3"><PayModeBadge mode={j.pay_mode} testid={`pay-mode-${j.id}`} />{j.is_test && <span className="ml-1 inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border border-fuchsia-400 bg-fuchsia-50 text-fuchsia-800" data-testid={`job-test-badge-${j.id}`}>Test</span>}</td>
                                            {!tech && <td className="px-6 py-3 text-right">
                                                {pct === null ? <span className="text-slate-300 text-xs">—</span> : (
                                                    <span data-testid={`plan-usage-${j.id}`}
                                                          className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${
                                                              pct > 100 ? "bg-red-100 text-red-700 border-red-300"
                                                              : pct >= 80 ? "bg-orange-100 text-orange-700 border-orange-300"
                                                              : "bg-emerald-50 text-emerald-700 border-emerald-300"}`}>
                                                        {pct}% plánu{j.plan_basis === "rozpocet" ? " (rozpočet)" : ""}
                                                    </span>
                                                )}
                                            </td>}
                                            {!tech && <td className="px-6 py-3 text-right font-semibold">{fmt(total)}</td>}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {!tech && <Fab icon={Plus} label="Nová zakázka" to="/zakazky/nova" testid="fab-new-job" />}
            {view === "kanban" && <JobsKanban jobs={filtered} onMove={tech ? () => toast.info("Fázi zakázky mění vlastník nebo administrátor") : moveStatus} />}
            {view === "calendar" && <JobsCalendar jobs={filtered} />}
        </div>
    );
}

function ViewBtn({ active, onClick, icon: Icon, children, testid }) {
    return (
        <button onClick={onClick} data-testid={testid}
                className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider inline-flex items-center gap-2 ${
                    active ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                }`}>
            <Icon className="w-3.5 h-3.5" /> {children}
        </button>
    );
}
